import React, { useEffect, useState } from 'react'
import '../../assets/theme/theme.less'
import { LayoutComponent as Layout, ButtonComponent as Button } from '../../components'
import { Row, Col, Form, Select, Switch, InputNumber, message as alert, Spin, Popconfirm, Tag, Pagination } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
    getAutomacao,
    updateAutomacao,
    deleteAutomacao,
    getAutomacaoLogs,
    runAutomacao,
    resetSuccessAction,
    clearAutomacao
} from '../../redux/slices/automacaoOperacaoSlice'
import { buildCronFromSchedule, parseCronToSchedule } from '../../utils/cronSchedule'
import { ReactComponent as iconChevron } from '../../assets/img/chevron-left.svg'
import AutomacaoLogsTable from './automacaoLogsTable'
import CronScheduleFields from './cronScheduleFields'

const { Option } = Select

const AutomacaoDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { id } = useParams()
    const [form] = Form.useForm()
    const modoCotacao = Form.useWatch('modo_cotacao', form)
    const [logsPage, setLogsPage] = useState(1)

    const {
        automacao,
        logs,
        logsPagination,
        isLoadingDetail,
        isLoadingLogs,
        isSubmit,
        successAction,
        message
    } = useSelector((state) => state.automacaoOperacao)

    useEffect(() => {
        dispatch(getAutomacao({ id, page: 1, limit: 20 }))
        return () => {
            dispatch(clearAutomacao())
        }
    }, [dispatch, id])

    useEffect(() => {
        if (automacao) {
            const schedule = parseCronToSchedule(automacao.horario_cron)
            form.setFieldsValue({
                enabled: !!automacao.enabled,
                dias_semana: schedule.dias_semana,
                horario: schedule.horario,
                modo_cotacao: automacao.modo_cotacao,
                cotacao_fixa: automacao.cotacao_fixa != null ? Number(automacao.cotacao_fixa) : null,
                valor_saida_padrao: automacao.valor_saida_padrao != null ? Number(automacao.valor_saida_padrao) : 0,
                percentual_diario: automacao.percentual_diario != null ? Number(automacao.percentual_diario) : null
            })
        }
    }, [automacao, form])

    useEffect(() => {
        if (message) {
            alert.error(message)
        }
    }, [message])

    useEffect(() => {
        if (successAction) {
            alert.success('Automação atualizada com sucesso!')
            dispatch(resetSuccessAction())
            dispatch(getAutomacao({ id, page: logsPage, limit: 20 }))
        }
    }, [successAction, dispatch, id, logsPage])

    const handleSubmit = async (values) => {
        try {
            await dispatch(updateAutomacao({
                id,
                enabled: !!values.enabled,
                horario_cron: buildCronFromSchedule(values.dias_semana, values.horario),
                modo_cotacao: values.modo_cotacao,
                cotacao_fixa: values.modo_cotacao === 'fixa' ? values.cotacao_fixa : null,
                valor_saida_padrao: values.valor_saida_padrao ?? 0,
                percentual_diario: values.percentual_diario != null && values.percentual_diario !== ''
                    ? values.percentual_diario
                    : null
            })).unwrap()
        } catch (error) {
            alert.error(error || 'Erro ao atualizar automação')
        }
    }

    const handleDelete = async () => {
        try {
            await dispatch(deleteAutomacao({ id })).unwrap()
            alert.success('Automação removida')
            navigate('/automacoes')
        } catch (error) {
            alert.error(error || 'Erro ao remover automação')
        }
    }

    const handleRun = async (force = false) => {
        try {
            const result = await dispatch(runAutomacao({ id, force })).unwrap()
            alert.success(result?.message || 'Automação executada com sucesso')
            dispatch(getAutomacao({ id, page: logsPage, limit: 20 }))
        } catch (error) {
            alert.error(error || 'Erro ao executar automação')
            dispatch(getAutomacaoLogs({ id, page: logsPage, limit: 20 }))
        }
    }

    const handleLogsPageChange = (page) => {
        setLogsPage(page)
        dispatch(getAutomacaoLogs({ id, page, limit: 20 }))
    }

    if (isLoadingDetail && !automacao) {
        return (
            <Layout width>
                <div className="d-flex justify-center mt-25">
                    <Spin size="large" />
                </div>
            </Layout>
        )
    }

    const product = automacao?.product || {}

    return (
        <Layout width>
            <Row>
                <Col md={24}>
                    <div className="d-flex justify-between align-center">
                        <div>
                            <h1 className="title page-title">Detalhes da Automação</h1>
                            <p className="tx-gray mt-10">
                                {product.title || 'Produto'} ({product.ticker || '-'}) — tipo {product.contract_type ?? '-'}
                                <Tag color={automacao?.enabled ? 'green' : 'default'} className="ml-10">
                                    {automacao?.enabled ? 'Ativa' : 'Inativa'}
                                </Tag>
                            </p>
                        </div>
                        <div style={{ width: 140 }}>
                            <Button
                                type="back"
                                icon={iconChevron}
                                htmlType="button"
                                onClick={() => navigate('/automacoes')}
                            >
                                Voltar
                            </Button>
                        </div>
                    </div>
                </Col>

                <Col md={24} className="mt-25">
                    <h2 className="title">Configuração</h2>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        className="mt-15"
                    >
                        <Row gutter={[16, 16]}>
                            <Col md={12}>
                                <Form.Item
                                    name="enabled"
                                    label={<span className="tx-pink font-bold">Ativar automação</span>}
                                    valuePropName="checked"
                                >
                                    <Switch checkedChildren="Ativa" unCheckedChildren="Inativa" />
                                </Form.Item>
                            </Col>
                            <Col md={12}>
                                <Form.Item
                                    name="modo_cotacao"
                                    label={<span className="tx-pink font-bold">Modo da cotação</span>}
                                    rules={[{ required: true, message: 'Selecione o modo' }]}
                                >
                                    <Select>
                                        <Option value="fixa">Cotação fixa</Option>
                                        <Option value="api">API (dólar do dia)</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <CronScheduleFields />
                            {modoCotacao === 'fixa' && (
                                <Col md={12}>
                                    <Form.Item
                                        name="cotacao_fixa"
                                        label={<span className="tx-pink font-bold">Cotação fixa (USD/BRL)</span>}
                                        rules={[{ required: true, message: 'Informe a cotação fixa' }]}
                                    >
                                        <InputNumber min={0.0001} step={0.01} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            )}
                            <Col md={12}>
                                <Form.Item
                                    name="percentual_diario"
                                    label={<span className="tx-pink font-bold">Percentual diário (%)</span>}
                                    extra="Se informado (> 0), calcula valorSaida = totalSaidaUsd anterior × (1 + %/100) e ignora o valor saída padrão"
                                >
                                    <InputNumber min={0} step={0.0001} precision={6} style={{ width: '100%' }} placeholder="Ex.: 0.0566" />
                                </Form.Item>
                            </Col>
                            <Col md={12}>
                                <Form.Item
                                    name="valor_saida_padrao"
                                    label={<span className="tx-pink font-bold">Valor saída padrão</span>}
                                    extra="Usado apenas se o % diário estiver vazio ou zero. Use 0 para replicar saldos"
                                    rules={[{ required: true, message: 'Informe o valor de saída' }]}
                                >
                                    <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <div className="mt-25 d-flex align-center">
                            <div style={{ width: 220 }} className="mr-10">
                                <Button type="primary" active htmlType="submit" loading={isSubmit}>
                                    Salvar alterações
                                </Button>
                            </div>
                            <div style={{ width: 180 }} className="mr-10">
                                <Popconfirm
                                    title="Executar a liquidação agora com a configuração atual?"
                                    onConfirm={() => handleRun(false)}
                                    okText="Executar"
                                    cancelText="Cancelar"
                                >
                                    <Button type="default" htmlType="button" loading={isSubmit}>
                                        Executar agora
                                    </Button>
                                </Popconfirm>
                            </div>
                            <div style={{ width: 140 }}>
                                <Popconfirm
                                    title="Remover esta automação? Ela precisa estar inativa."
                                    onConfirm={handleDelete}
                                    okText="Remover"
                                    cancelText="Cancelar"
                                >
                                    <Button type="danger" htmlType="button" loading={isSubmit}>
                                        Excluir
                                    </Button>
                                </Popconfirm>
                            </div>
                        </div>
                    </Form>
                </Col>

                <Col md={24} className="mt-25">
                    <h2 className="title">Histórico de execuções</h2>
                    <p className="tx-gray mt-10">
                        Registros gerados quando o cron (ou uma execução manual) disparar a liquidação deste produto.
                    </p>
                    <div className="mt-15">
                        <AutomacaoLogsTable logs={logs} loading={isLoadingLogs || isLoadingDetail} />
                    </div>
                    {logsPagination?.totalPages > 1 && (
                        <div className="mt-15 d-flex justify-center">
                            <Pagination
                                current={logsPagination.page || logsPage}
                                total={logsPagination.total || 0}
                                pageSize={logsPagination.limit || 20}
                                onChange={handleLogsPageChange}
                                showSizeChanger={false}
                            />
                        </div>
                    )}
                </Col>
            </Row>
        </Layout>
    )
}

export default AutomacaoDetail
